/**
 * WelcomePageEditor - the shared welcome-page authoring surface.
 *
 * Controlled component: the parent owns the `page` state and persistence (the
 * org Settings > Welcome page tab loads and saves through the org-scoped
 * endpoint). This component renders the section toggles, the content-block list
 * editor, the block edit dialog, and the live preview - which reuses the exact
 * `WelcomeBanner` the consumer landing page renders, so the preview is always
 * faithful.
 *
 * `uploadFn` is optional: org admins omit it and ImageUpload uses the default
 * `/files/upload/`; a caller lacking an org-user audience can pass a scoped
 * uploader instead.
 */

import { useMemo, useState } from 'react'
import {
  ArrowUp,
  ArrowDown,
  Plus,
  Pencil,
  Trash2,
  ImageIcon,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ImageUpload } from '@/admin/components/image-upload'
import { useForm } from '@/shared/lib/forms'
import type { FormField, FormContext } from '@/shared/lib/forms'
import {
  WelcomeBanner,
  type WelcomePageContentBlock,
  type WelcomeBlockType,
} from '@/shared/components/welcome-banner'
import type { UploadedFile } from '@/admin/hooks/use-products'
import type { WelcomePageState } from '@/admin/components/welcome-page-editor-utils'

type UploadFn = (args: { file: File; use_case: string }) => Promise<UploadedFile>

const BLOCK_TYPE_LABELS: Record<WelcomeBlockType, string> = {
  COVER: 'Cover image',
  OVERLAY: 'Image with overlay text',
  TEXT_ONLY: 'Text only',
  SPOTLIGHT: 'Spotlight (image + text)',
  EXAMPLE_PRODUCT: 'Example product',
}

const BLOCK_TYPE_OPTIONS = (
  Object.keys(BLOCK_TYPE_LABELS) as WelcomeBlockType[]
).map((value) => ({ value, label: BLOCK_TYPE_LABELS[value] }))

function newBlock(): WelcomePageContentBlock {
  return {
    content_type: 'TEXT_ONLY',
    block_title: '',
    block_body: '',
    block_image: null,
    block_cta_type: null,
    block_cta_content: '',
    block_cta_url: '',
    content_alignment: 'LEFT',
  }
}

const FORM_ID = 'welcome-block-form'

// Block types that show an image upload (EXAMPLE_PRODUCT uses the product image).
const IMAGE_TYPES: WelcomeBlockType[] = ['COVER', 'OVERLAY', 'SPOTLIGHT']
const TITLE_TYPES: WelcomeBlockType[] = [
  'OVERLAY',
  'TEXT_ONLY',
  'SPOTLIGHT',
  'EXAMPLE_PRODUCT',
]
const ALIGN_TYPES: WelcomeBlockType[] = ['OVERLAY', 'SPOTLIGHT']
const CTA_TYPES: WelcomeBlockType[] = [
  'OVERLAY',
  'TEXT_ONLY',
  'SPOTLIGHT',
  'EXAMPLE_PRODUCT',
]

const isType = (ctx: FormContext, types: WelcomeBlockType[]) =>
  types.includes(ctx.values.content_type as WelcomeBlockType)

export function WelcomePageEditor({
  page,
  onChange,
  orgName,
  uploadFn,
}: {
  page: WelcomePageState
  onChange: (next: WelcomePageState) => void
  orgName: string
  uploadFn?: UploadFn
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  function moveBlock(index: number, direction: -1 | 1) {
    const blocks = [...page.content_blocks]
    const target = index + direction
    if (target < 0 || target >= blocks.length) return
    ;[blocks[index], blocks[target]] = [blocks[target], blocks[index]]
    onChange({ ...page, content_blocks: blocks })
  }

  function deleteBlock(index: number) {
    onChange({
      ...page,
      content_blocks: page.content_blocks.filter((_, i) => i !== index),
    })
  }

  function addBlock() {
    onChange({
      ...page,
      content_blocks: [...page.content_blocks, newBlock()],
    })
    setEditingIndex(page.content_blocks.length)
  }

  function saveBlock(index: number, block: WelcomePageContentBlock) {
    const blocks = [...page.content_blocks]
    blocks[index] = block
    onChange({ ...page, content_blocks: blocks })
    setEditingIndex(null)
  }

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,380px)_1fr]">
      {/* ── Editor column ── */}
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
          <div className="clara-section-label">Sections</div>
          <div className="flex items-center justify-between">
            <Label className="text-[13px] text-[var(--ink-2)]">
              Show FAQ section
            </Label>
            <Switch
              checked={page.include_faqs}
              onCheckedChange={(v) => onChange({ ...page, include_faqs: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-[13px] text-[var(--ink-2)]">
              Show contact section
            </Label>
            <Switch
              checked={page.include_contact}
              onCheckedChange={(v) => onChange({ ...page, include_contact: v })}
            />
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="clara-section-label">Content blocks</div>
            <Button variant="outline" size="xs" onClick={addBlock}>
              <Plus className="mr-1 h-3 w-3" />
              Add block
            </Button>
          </div>

          {page.content_blocks.length === 0 ? (
            <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] px-4 py-10 text-center text-[13px] text-[var(--ink-3)]">
              No content blocks yet. Add one to get started.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {page.content_blocks.map((block, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 py-2.5"
                >
                  <div className="flex flex-col gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      disabled={index === 0}
                      onClick={() => moveBlock(index, -1)}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      disabled={index === page.content_blocks.length - 1}
                      onClick={() => moveBlock(index, 1)}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        {BLOCK_TYPE_LABELS[block.content_type]}
                      </Badge>
                      {block.block_image?.file_path && (
                        <ImageIcon className="h-3 w-3 text-[var(--ink-3)]" />
                      )}
                    </div>
                    <div className="mt-1 truncate text-[13px] text-[var(--ink-2)]">
                      {block.block_title || block.block_body || 'Untitled block'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setEditingIndex(index)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => deleteBlock(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--status-error)]" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Live preview column ── */}
      <div className="flex flex-col gap-3">
        <div className="clara-section-label">Live preview</div>
        <div className="overflow-hidden rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
          <div className="flex flex-col gap-14 p-6 md:p-10">
            {page.content_blocks.length === 0 ? (
              <div className="py-16 text-center text-[13px] text-[var(--ink-3)]">
                Your welcome page preview appears here.
              </div>
            ) : (
              page.content_blocks.map((block, index) => (
                <WelcomeBanner key={index} block={block} orgName={orgName} />
              ))
            )}
          </div>
        </div>
      </div>

      {editingIndex !== null && page.content_blocks[editingIndex] && (
        <BlockEditDialog
          block={page.content_blocks[editingIndex]}
          uploadFn={uploadFn}
          onClose={() => setEditingIndex(null)}
          onSave={(block) => saveBlock(editingIndex, block)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Block edit dialog (generated form)
// ---------------------------------------------------------------------------

function BlockEditDialog({
  block,
  uploadFn,
  onClose,
  onSave,
}: {
  block: WelcomePageContentBlock
  uploadFn?: UploadFn
  onClose: () => void
  onSave: (block: WelcomePageContentBlock) => void
}) {
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <BlockEditForm
          block={block}
          uploadFn={uploadFn}
          onClose={onClose}
          onSave={onSave}
        />
      </DialogContent>
    </Dialog>
  )
}

function BlockEditForm({
  block,
  uploadFn,
  onClose,
  onSave,
}: {
  block: WelcomePageContentBlock
  uploadFn?: UploadFn
  onClose: () => void
  onSave: (block: WelcomePageContentBlock) => void
}) {
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'content_type',
        label: 'Block type',
        value: block.content_type,
        options: BLOCK_TYPE_OPTIONS,
        required: 'Block type is required.',
      },
      {
        type: 'text',
        name: 'block_title',
        label: 'Title',
        value: block.block_title ?? '',
        visible: (ctx) => isType(ctx, TITLE_TYPES),
      },
      {
        type: 'textarea',
        name: 'block_body',
        label: 'Body',
        value: block.block_body ?? '',
        visible: (ctx) => isType(ctx, TITLE_TYPES),
      },
      {
        type: 'select',
        name: 'content_alignment',
        label: 'Image alignment',
        value: block.content_alignment ?? 'LEFT',
        options: [
          { value: 'LEFT', label: 'Left' },
          { value: 'RIGHT', label: 'Right' },
        ],
        visible: (ctx) => isType(ctx, ALIGN_TYPES),
      },
      {
        type: 'custom',
        name: 'block_image',
        label: 'Image',
        value: block.block_image ?? null,
        visible: (ctx) => isType(ctx, IMAGE_TYPES),
        render: (ctx) => {
          const img = ctx.values.block_image as
            | WelcomePageContentBlock['block_image']
            | null
          return (
            <ImageUpload
              label="Image"
              useCase="ORGANISATION_SPLASH"
              uploadFn={uploadFn}
              value={img?.uuid ?? null}
              previewUrl={img?.file_path ?? null}
              onChange={(uuidValue, file) =>
                ctx.setFieldValue(
                  'block_image',
                  uuidValue && file
                    ? {
                        uuid: uuidValue,
                        file_path: file.file_url,
                        file_name: file.file_name,
                      }
                    : null,
                )
              }
            />
          )
        },
      },
      {
        // 'NONE' sentinel: Radix SelectItem rejects an empty-string value.
        type: 'select',
        name: 'block_cta_type',
        label: 'Button',
        value: block.block_cta_type ?? 'NONE',
        options: [
          { value: 'NONE', label: 'No button' },
          { value: 'INTERNAL_REGISTRATION', label: 'Register a product' },
          { value: 'EXTERNAL_LINK', label: 'External link' },
        ],
        visible: (ctx) => isType(ctx, CTA_TYPES),
      },
      {
        type: 'text',
        name: 'block_cta_content',
        label: 'Button text',
        value: block.block_cta_content ?? '',
        visible: (ctx) =>
          Boolean(ctx.values.block_cta_type) &&
          ctx.values.block_cta_type !== 'NONE',
      },
      {
        type: 'text',
        name: 'block_cta_url',
        label: 'Link URL',
        placeholder: 'https://example.com',
        value: block.block_cta_url ?? '',
        visible: (ctx) => ctx.values.block_cta_type === 'EXTERNAL_LINK',
      },
    ],
    [block, uploadFn],
  )

  const { Form } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (values) => {
      const v = values as Record<string, unknown>
      const contentType = v.content_type as WelcomeBlockType
      const rawCta = (v.block_cta_type as string) || null
      const ctaType = rawCta === 'NONE' ? null : rawCta
      onSave({
        content_type: contentType,
        block_title: TITLE_TYPES.includes(contentType)
          ? ((v.block_title as string) || null)
          : null,
        block_body: TITLE_TYPES.includes(contentType)
          ? ((v.block_body as string) || null)
          : null,
        block_image: IMAGE_TYPES.includes(contentType)
          ? ((v.block_image as WelcomePageContentBlock['block_image']) ?? null)
          : null,
        block_cta_type: CTA_TYPES.includes(contentType)
          ? (ctaType as WelcomePageContentBlock['block_cta_type'])
          : null,
        block_cta_content:
          CTA_TYPES.includes(contentType) && ctaType
            ? ((v.block_cta_content as string) || null)
            : null,
        block_cta_url:
          ctaType === 'EXTERNAL_LINK' ? ((v.block_cta_url as string) || null) : null,
        content_alignment: ALIGN_TYPES.includes(contentType)
          ? ((v.content_alignment as WelcomePageContentBlock['content_alignment']) ??
            'LEFT')
          : null,
      })
    },
    [block, onSave],
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit block</DialogTitle>
      </DialogHeader>
      <div className="py-2">
        <Form className="flex flex-col gap-4" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" form={FORM_ID}>
          Done
        </Button>
      </DialogFooter>
    </>
  )
}
